import {
    Box,
    Collapse,
    CollapseIcon,
    Flex,
    MotionBox,
    Text,
    useTheme,
} from '@adamdotai/design-system'
import type { PropsWithChildren, RefObject } from 'react'
import React, { useEffect, useState } from 'react'

type CollapsableSectionProps = {
    title?: string
    note?: React.ReactNode
    defaultIsCollapsed?: boolean
    actions?: React.ReactNode
    icon?: React.ReactElement
    addIcon?: React.ReactElement
    childrenProps?: React.ComponentProps<typeof Box>
    HeaderContainerProps?: React.ComponentProps<typeof Flex>
    ParentContainerProps?: React.ComponentProps<typeof Box>
    customTitle?: React.ReactNode
    isDisabled?: boolean
    shouldExpand?: boolean
    setShouldExpandSection?: (shouldExpand: boolean) => void
    ref?: RefObject<{ open: VoidFunction; close: VoidFunction }>
}

export const CollapsableSection = React.memo(
    ({
        title,
        note,
        defaultIsCollapsed,
        children,
        actions,
        icon,
        childrenProps,
        HeaderContainerProps,
        ParentContainerProps,
        customTitle,
        isDisabled,
        addIcon,
        shouldExpand,
        setShouldExpandSection,
        ref,
    }: PropsWithChildren<CollapsableSectionProps>) => {
        const [isExpanded, setIsExpanded] = useState(!defaultIsCollapsed)
        const { direction } = useTheme()

        useEffect(() => {
            if (shouldExpand !== undefined) {
                setIsExpanded(shouldExpand)
            }
        }, [shouldExpand])

        useEffect(() => {
            if (ref != null) {
                ref.current = {
                    open: () => {
                        setIsExpanded(true)
                    },
                    close: () => {
                        setIsExpanded(false)
                    },
                }
            }
        }, [ref])

        return (
            <Box data-testid="collapsableSection" {...ParentContainerProps}>
                <Flex
                    justify="space-between"
                    align="center"
                    onClick={() => {
                        if (isDisabled) return

                        setIsExpanded(!isExpanded)
                        if (setShouldExpandSection) {
                            setShouldExpandSection(!isExpanded)
                        }
                    }}
                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                    {...HeaderContainerProps}
                    opacity={isDisabled ? '.5' : ''}
                >
                    {customTitle}
                    {!customTitle && (
                        <Flex gap={2} align="center">
                            {icon}
                            <Text fontWeight="bold">{title}</Text>
                        </Flex>
                    )}
                    <Flex gap={2} alignItems="center">
                        {actions}
                        {addIcon}

                        <MotionBox
                            animate={{
                                rotate: isExpanded
                                    ? 0
                                    : direction === 'ltr'
                                      ? -90
                                      : 90,
                            }}
                        >
                            <CollapseIcon />
                        </MotionBox>
                    </Flex>
                </Flex>
                <Box
                    sx={{
                        '& .chakra-collapse': {
                            /* To prevent the last item with shadow inside a collapse section from being cut off, the overflow property for the section wrapper was reset to unset. */
                            overflow: 'unset !important',
                        },
                    }}
                >
                    <Collapse isOpen={isExpanded}>
                        {/* TODO to pass notes from the children and remove padding from inside the component */}
                        {note}
                        <Box pt={4} {...childrenProps}>
                            {children}
                        </Box>
                    </Collapse>
                </Box>
            </Box>
        )
    },
)
CollapsableSection.displayName = 'CollapsableSection'
